import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import { useOrganizationId } from './useOrganizationId';
import { useUserProfile } from '@/contexts/UserProfileContext';

// ContactRole and ACTIVE_CONTACT_ROLES live in `src/types/domain.ts`.
// Re-exported here for backward compatibility with existing import sites.
export { ACTIVE_CONTACT_ROLES } from '@/types/domain';
export type { ContactRole } from '@/types/domain';
import type { ContactRole } from '@/types/domain';


export interface Contact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: ContactRole;
  company: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  notes: string | null;
  processing_fee_percentage: number | null;
  monthly_retainer_fee: number | null;
  is_active: boolean | null;
  profile_image_url: string | null;
  user_email: string | null;
  last_follow_up_date: string | null;
  follow_up_frequency: string | null;
  next_follow_up_date: string | null;
  auto_generate_tasks: boolean | null;
  planning_notes: string | null;
  created_at: string;
  updated_at: string;
  total_revenue?: number;
}

// Extended Contact interface with access control info
export interface ContactWithAccess extends Contact {
  has_full_access?: boolean;
  is_org_member?: boolean;
}

// Contacts are semi-static - 30 minute cache
const CONTACT_CACHE_TIME = 30 * 60 * 1000;

export const useContacts = () => {
  const { session } = useUserProfile();
  const organizationId = useOrganizationId();

  return useQuery({
    queryKey: ['contacts', organizationId],
    staleTime: CONTACT_CACHE_TIME,
    gcTime: CONTACT_CACHE_TIME * 2,
    refetchOnMount: false,
    queryFn: async () => {
      // Use RPC function that masks sensitive data for non-owners
      const { data: contacts, error } = await supabase
        .rpc('get_contacts_with_access_control');
      if (error) {
        // Fallback to direct query if RPC fails (e.g., function doesn't exist yet)
        logger.warn('RPC get_contacts_with_access_control failed, falling back to direct query:', error);
        const { data: fallbackContacts, error: fallbackError } = await supabase
          .from('contacts')
          .select('*')
          .order('name');

        if (fallbackError) throw fallbackError;
        return (fallbackContacts || []) as Contact[];
      }

      // Get artist IDs for revenue calculation
      const artistIds = (contacts || [])
        .filter((c: any) => c.role === 'artist')
        .map((c: any) => c.id);

      // Fetch all artist revenues in ONE query instead of N queries
      let revenueByContact: Record<string, number> = {};
      if (artistIds.length > 0) {
        const { data: revenueShares } = await supabase
          .from('revenue_shares')
          .select('contact_id, calculated_amount')
          .in('contact_id', artistIds)
          .eq('status', 'paid');

        // Aggregate revenues by contact in memory
        revenueByContact = (revenueShares || []).reduce((acc, share) => {
          if (share.contact_id) {
            acc[share.contact_id] = (acc[share.contact_id] || 0) + (share.calculated_amount || 0);
          }
          return acc;
        }, {} as Record<string, number>);
      }

      // Resolve organization member contact IDs server-side (works even when sensitive fields are masked)
      const orgMemberContactIds = new Set<string>();
      if (organizationId) {
        const { data: organizationMembers, error: orgMembersError } = await supabase
          .rpc('get_organization_members_with_names', { _organization_id: organizationId });

        if (orgMembersError) {
          logger.warn('RPC get_organization_members_with_names failed, falling back to direct join:', orgMembersError);

          // Fallback: derive org members → contact_id directly via user_profiles.
          // Keeps the "Team" tab populated even if the RPC ever fails again.
          const { data: members } = await supabase
            .from('organization_members')
            .select('user_id')
            .eq('organization_id', organizationId);

          const userIds = (members || []).map((m: any) => m.user_id).filter(Boolean);
          if (userIds.length > 0) {
            const { data: profiles } = await supabase
              .from('user_profiles')
              .select('user_id, contact_id')
              .in('user_id', userIds);

            (profiles || []).forEach((p: any) => {
              if (p.contact_id) orgMemberContactIds.add(p.contact_id);
            });
          }
        } else {
          (organizationMembers || []).forEach((member: any) => {
            if (member.contact_id) {
              orgMemberContactIds.add(member.contact_id);
            }
          });
        }
      }

      // Map contacts with pre-calculated revenues and org membership
      const contactsWithRevenue = (contacts || []).map((contact: any) => ({
        ...contact,
        total_revenue: contact.role === 'artist' ? (revenueByContact[contact.id] || 0) : undefined,
        is_org_member: orgMemberContactIds.has(contact.id),
      }));

      return contactsWithRevenue as ContactWithAccess[];
    },
    enabled: !!session,
  });
};

export const useContact = (id: string | undefined) => {
  return useQuery({
    queryKey: ['contact', id],
    queryFn: async () => {
      if (!id) return null;
      
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as Contact;
    },
    enabled: !!id,
  });
};

export const useCreateContact = () => {
  const queryClient = useQueryClient();
  const organizationId = useOrganizationId();

  return useMutation({
    mutationFn: async (contact: Partial<Omit<Contact, 'id' | 'created_at' | 'updated_at' | 'total_revenue'>> & { name: string; role: ContactRole }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('contacts')
        .insert([{ ...contact, created_by_user_id: user.id, organization_id: organizationId } as any])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['artist-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['organizer-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['user-profiles'] });
      queryClient.invalidateQueries({ queryKey: ['venue-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['venue-managers'] });
      queryClient.invalidateQueries({ queryKey: ['org-contacts-dropdown'] });
      toast({
        title: 'Success',
        description: 'Contact created successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Contact> & { id: string }) => {
      const { data, error } = await supabase
        .from('contacts')
        .update(updates as any)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) throw error;
      
      if (!data) {
        logger.warn('Update successful but no data returned due to RLS policies');
        return { id, ...updates } as any;
      }
      
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contact', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['artist-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['organizer-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['user-profiles'] });
      queryClient.invalidateQueries({ queryKey: ['venue-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['venue-managers'] });
      queryClient.invalidateQueries({ queryKey: ['org-contacts-dropdown'] });
      toast({
        title: 'Success',
        description: 'Contact updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useDeactivateContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('contacts')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['artist-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['organizer-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['user-profiles'] });
      queryClient.invalidateQueries({ queryKey: ['venue-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['venue-managers'] });
      toast({
        title: 'Success',
        description: 'Contact deactivated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Keep the old name for backwards compatibility, but it now deactivates instead of deleting
export const useDeleteContact = useDeactivateContact;

export const useUserProfiles = () => {
  return useQuery({
    queryKey: ['user-profiles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('user_id, contact_id')
        .order('created_at');

      if (error) throw error;

      // Get user emails from auth.users using the function
      const { data: usersData, error: usersError } = await supabase
        .rpc('get_all_users_with_emails');

      if (usersError) throw usersError;

      return { profiles: data, users: usersData };
    },
  });
};

// Hook for dropdown contacts in TaskDialog - returns ALL contacts in the organization
export interface DropdownContact {
  id: string;
  name: string;
  role: ContactRole;
}

export const useOrgContactsForDropdown = (organizationId: string | null) => {
  return useQuery({
    queryKey: ['org-contacts-dropdown', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      
      const { data, error } = await supabase
        .rpc('get_org_contacts_for_dropdown', { _org_id: organizationId });

      if (error) throw error;
      return (data || []) as DropdownContact[];
    },
    enabled: !!organizationId,
  });
};
